import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  Suspense,
} from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import {
  ArrowRight,
  Star,
  ShoppingCart,
  Heart,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Clock,
  Award,
  Users,
  Package,
  Play,
  Sparkles,
  Eye,
} from "lucide-react";
import HeroSlider from "../final_components/HeroSlider";
import HorizontalProductScroll from "./HorizontalProductScroll";
import { useWishlist } from "../../hooks/useWishlist";
import { useAuth } from "../../context/AuthContext";
import { useProducts } from "../../hooks/useProducts";
import { apiRequest, submitContact, getApiErrorMessage } from "../../config/api";
import toast from "react-hot-toast";
import { normalizeEmail, isValidEmailFormat } from "../../utils/emailUtils";
import {
  allYarnProducts,
  allAccessoryProducts,
  topSellingYarns,
  newYarns,
  topSellingAccessories,
  newAccessories,
} from "./ProductData";
import {
  SkeletonHero,
  SkeletonSection,
  SkeletonHorizontalScroll,
} from "./SkeletonLoader";
import LightLoader from "./LightLoader";
import HomePageLoader from "./HomePageLoader";
import PageLoader from "./PageLoader";

// Memoized product filtering function
const getFilteredProducts = (productList, idArray) => {
  return idArray
    .map((id) => productList.find((product) => product.id === id))
    .filter(Boolean);
};

// Memoized ProductHoverCard component
const ProductHoverCard = React.memo(({ product }) => {
  const [isHovered, setIsHovered] = useState(false);
  const {
    toggleWishlist,
    isInWishlist,
    loading: wishlistLoading,
  } = useWishlist();
  const { user } = useAuth();

  const handleProductClick = useCallback(() => {
    window.open(`https://knotsandkrafts.in/shop/${product.id}`, "_blank");
  }, [product.id]);

  const handleOverlayClick = useCallback(
    (e) => {
      e.stopPropagation();
      window.open(`https://knotsandkrafts.in/shop/${product.id}`, "_blank");
    },
    [product.id],
  );

  const handleWishlistClick = useCallback(
    async (e) => {
      e.stopPropagation();
      if (!user) {
        toast.error("Please login to manage wishlist");
        return;
      }
      try {
        await toggleWishlist(product.id);
      } catch (error) {
        console.error("Failed to toggle wishlist:", error);
      }
    },
    [product.id, toggleWishlist, user],
  );

  const handleCartClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      className="relative group cursor-pointer bg-white border border-gray-200 transition-all duration-300 hover:shadow-lg"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Product Image */}
      <div className="relative overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />

        {/* Badge */}
        {product.badge && (
          <div className="absolute top-3 left-3">
            <span
              className={`px-3 py-1 text-xs font-semibold ${
                product.badge === "Sale"
                  ? "bg-red-500 text-white"
                  : product.badge === "New"
                    ? "bg-green-500 text-white"
                    : product.badge === "Best Seller"
                      ? "bg-primary-600 text-white"
                      : "bg-gray-800 text-white"
              }`}
            >
              {product.badge}
            </span>
          </div>
        )}

        {/* Hover Overlay with Actions */}
        <div
          className={`absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center gap-2 transition-opacity duration-300 ${
            isHovered ? "opacity-100" : "opacity-0"
          }`}
        >
          <button
            onClick={handleOverlayClick}
            className="p-3 bg-white text-secondary-900 hover:bg-primary-600 hover:text-white transition-colors"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={handleWishlistClick}
            disabled={wishlistLoading}
            className="p-3 bg-white text-secondary-900 hover:bg-primary-600 hover:text-white transition-colors disabled:opacity-50"
          >
            <Heart
              className={`w-5 h-5 transition-colors ${
                isInWishlist(product.id)
                  ? "text-red-500 fill-current"
                  : "text-gray-400"
              }`}
              fill={isInWishlist(product.id) ? "currentColor" : "none"}
            />
          </button>
          <button
            onClick={handleCartClick}
            className="p-3 bg-white text-secondary-900 hover:bg-primary-600 hover:text-white transition-colors"
          >
            <ShoppingCart className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4" onClick={handleProductClick}>
        <h3 className="text-sm font-semibold text-secondary-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {product.name}
        </h3>

        {/* Price with MRP */}
        <div className="flex flex-col mb-3">
          {product.originalPrice && product.originalPrice > product.price ? (
            <>
              <span className="text-sm text-secondary-500 line-through">
                MRP ₹{product.originalPrice}
              </span>
              <span className="text-lg font-bold text-secondary-900">
                ₹{product.price}
              </span>
            </>
          ) : (
            <span className="text-lg font-bold text-secondary-900">
              MRP ₹{product.price}
            </span>
          )}
        </div>

        {/* Rating */}
        <div className="flex items-center mb-3">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <svg
                key={i}
                className={`h-3 w-3 ${
                  i < Math.floor(product.rating)
                    ? "text-yellow-400 fill-current"
                    : "text-gray-300"
                }`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <span className="ml-2 text-xs text-secondary-600">
            ({product.reviews})
          </span>
        </div>
      </div>
    </div>
  );
});

ProductHoverCard.displayName = "ProductHoverCard";

const HomePage = React.memo(() => {
  const [activeTab, setActiveTab] = useState("topSelling");
  const [activeAccessoryTab, setActiveAccessoryTab] = useState("topSelling");
  const categoriesRef = useRef([]);

  // Contact form state
  const [isSending, setIsSending] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  // Page loading state
  const [pageLoading, setPageLoading] = useState(true);
  const [pageReady, setPageReady] = useState(false);

  // Data loading states
  const [heroLoading, setHeroLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // All accessory product IDs from API (for accessories slider to show full catalog)
  const [allAccessoryIds, setAllAccessoryIds] = useState([]);

  useEffect(() => {
    // Simulate loading time for better UX
    const timer = setTimeout(() => {
      setPageLoading(false);
      setPageReady(true);
    }, 1500);

    // Simulate section loading times
    const heroTimer = setTimeout(() => setHeroLoading(false), 800);
    const productsTimer = setTimeout(() => setProductsLoading(false), 1200);
    const categoriesTimer = setTimeout(() => setCategoriesLoading(false), 1000);

    return () => {
      clearTimeout(timer);
      clearTimeout(heroTimer);
      clearTimeout(productsTimer);
      clearTimeout(categoriesTimer);
    };
  }, []);

  // Fetch all accessories for the accessories slider (Top Selling + New tabs show full list)
  useEffect(() => {
    let cancelled = false;
    const fetchAllAccessories = async () => {
      try {
        const res = await apiRequest("/products/accessories?limit=100&page=1");
        if (cancelled || !res?.data) return;
        const ids = (res.data || []).map((p) => p.id).filter(Boolean);
        setAllAccessoryIds(ids);
      } catch (err) {
        console.error("Failed to fetch all accessories for homepage:", err);
      }
    };
    fetchAllAccessories();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const email = normalizeEmail(contactForm.email);

    if (!contactForm.name.trim()) {
      toast.error("Please enter your name");
      return;
    }

    if (!isValidEmailFormat(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!contactForm.message.trim() || contactForm.message.trim().length < 10) {
      toast.error("Message must be at least 10 characters");
      return;
    }

    setIsSending(true);

    try {
      await submitContact({
        name: contactForm.name.trim(),
        email,
        subject: contactForm.subject.trim() || "Website inquiry",
        message: contactForm.message.trim(),
      });
      toast.success("Message sent successfully!");
      setContactForm({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Failed to send the message. Please try again."),
      );
    } finally {
      setIsSending(false);
    }
  };

  // Memoized filtered product arrays
  const filteredTopSellingYarns = useMemo(
    () => getFilteredProducts(allYarnProducts, topSellingYarns),
    [],
  );
  const filteredNewYarns = useMemo(
    () => getFilteredProducts(allYarnProducts, newYarns),
    [],
  );
  const filteredTopSellingAccessories = useMemo(
    () => getFilteredProducts(allAccessoryProducts, topSellingAccessories),
    [],
  );
  const filteredNewAccessories = useMemo(
    () => getFilteredProducts(allAccessoryProducts, newAccessories),
    [],
  );

  // Merged accessory IDs: curated first, then all others from API (so slider shows full catalog)
  const mergedTopSellingAccessoryIds = useMemo(() => {
    const curated = topSellingAccessories;
    const rest = (allAccessoryIds || []).filter((id) => !curated.includes(id));
    return [...curated, ...rest];
  }, [allAccessoryIds]);

  const mergedNewAccessoryIds = useMemo(() => {
    const curated = newAccessories;
    const rest = (allAccessoryIds || []).filter((id) => !curated.includes(id));
    return [...curated, ...rest];
  }, [allAccessoryIds]);

  // Memoized hero slides data
  const heroSlides = useMemo(
    () => [
      {
        id: 1,
        title: "Krafty Chenille Baby",
        subtitle: "Luxuriously Soft Yarns",
        tagline: "Premium Collection",
        description:
          "Luxuriously soft, perfect for delicate and cuddly creations. Experience the ultimate comfort for your precious projects.",
        image: "./chenille.jpg",
        cta: "Shop Now",
        secondaryCta: "Learn More",
        link: "https://knotsandkrafts.in/shop/66d40068c96124ed761689ad",
        badge: "Best Seller",
        gradient: "from-pink-500/20 to-purple-500/20",
      },
      {
        id: 2,
        title: "Krafty Baby Milk Cotton",
        subtitle: "The Softest Touch",
        tagline: "Organic & Natural",
        description:
          "Designed for the softest, snuggliest creations. Hypoallergenic and perfect for sensitive skin.",
        image: "./krafty-milk-cotton.jpg",
        cta: "Shop Now",
        secondaryCta: "Touch Guide",
        link: "https://knotsandkrafts.in/shop/67f915ed4f1f8e7634c8c9b5",
        badge: "Organic",
        gradient: "from-blue-500/20 to-cyan-500/20",
      },
      {
        id: 3,
        title: "T Shirt Yarn",
        subtitle: "Versatile & Stretchy",
        tagline: "Eco-Friendly Choice",
        description:
          "Soft and stretchy, ideal for crafting bags, rugs, and home decor. Sustainable crafting for a better tomorrow.",
        image: "./tshirt-yarn.webp",
        cta: "Shop Now",
        secondaryCta: "Sustainability",
        link: "https://knotsandkrafts.in/shop/66d40079c96124ed76169ea4",
        badge: "Eco-Friendly",
        gradient: "from-green-500/20 to-emerald-500/20",
      },
      {
        id: 4,
        title: "Softly Baby",
        subtitle: "Light & Airy",
        tagline: "Cloud-Like Comfort",
        description:
          "Light and airy, designed for crafting tender, cuddly projects. Perfect for delicate baby items.",
        image: "./softly-baby.webp",
        cta: "Shop Now",
        secondaryCta: "Feel the Difference",
        link: "https://knotsandkrafts.in/shop/66d40068c96124ed7616857e",
        badge: "Premium",
        gradient: "from-yellow-500/20 to-orange-500/20",
      },
      {
        id: 5,
        title: "Stylecraft Special DK",
        subtitle: "Superior Quality",
        tagline: "Professional Choice",
        description:
          "Rich colours and superior softness for all your crafting needs. Trusted by professionals worldwide.",
        image: "./stylecraft-dk.jpg",
        cta: "Shop Now",
        secondaryCta: "Color Chart",
        link: "https://knotsandkrafts.in/shop/66d40068c96124ed76168851",
        badge: "Professional",
        gradient: "from-red-500/20 to-pink-500/20",
      },
      {
        id: 6,
        title: "Papatya Cake Silver",
        subtitle: "Versatile Crafting",
        tagline: "Multi-Purpose Yarn",
        description:
          "Perfect for making all kinds of projects, from cardigans and blankets to scarves, sweaters and more!",
        image: "./papatya-cake.jpg",
        cta: "Shop Now",
        secondaryCta: "Project Ideas",
        link: "https://knotsandkrafts.in/shop/66d40068c96124ed76168975",
        badge: "Versatile",
        gradient: "from-indigo-500/20 to-purple-500/20",
      },
      {
        id: 7,
        title: "Cicibebe Batik",
        subtitle: "Unique Craftwork",
        tagline: "Artistic Expression",
        description:
          "Richly coloured batik yarn, soft and perfect for unique craftwork. Create one-of-a-kind pieces.",
        image: "./cicibebe-batik.jpg",
        cta: "Shop Now",
        secondaryCta: "Gallery",
        link: "https://knotsandkrafts.in/shop/66d40068c96124ed7616893f",
        badge: "Unique",
        gradient: "from-teal-500/20 to-blue-500/20",
      },
    ],
    [],
  );

  // Memoized top products data
  const topProducts = useMemo(
    () => [
      {
        id: 1,
        name: "Krafty Chenille Baby",
        price: 249.99,
        originalPrice: null,
        image: "./chenille.jpg",
        rating: 4.9,
        reviews: 245,
        badge: "Best Seller",
        link: "https://knotsandkrafts.in/shop/66d40068c96124ed761689ad",
      },
      {
        id: 2,
        name: "Stylecraft Special DK",
        price: 189.99,
        originalPrice: null,
        image: "./stylecraft-dk.jpg",
        rating: 4.8,
        reviews: 189,
        badge: "Top Rated",
        link: "https://knotsandkrafts.in/shop/66d40068c96124ed76168851",
      },
      {
        id: 3,
        name: "T Shirt Yarn",
        price: 149.99,
        originalPrice: 199.99,
        image: "./tshirt-yarn.webp",
        rating: 4.7,
        reviews: 156,
        badge: "Sale",
        link: "https://knotsandkrafts.in/shop/66d40079c96124ed76169ea4",
      },
      {
        id: 4,
        name: "Krafty Baby Milk Cotton",
        price: 199.99,
        originalPrice: null,
        image: "./krafty-milk-cotton.jpg",
        rating: 4.6,
        reviews: 98,
        badge: "Popular",
        link: "https://knotsandkrafts.in/shop/67f915ed4f1f8e7634c8c9b5",
      },
    ],
    [],
  );

  // Memoized latest products data
  const latestProducts = useMemo(
    () => [
      {
        id: 5,
        name: "Softly Baby",
        price: 179.99,
        originalPrice: null,
        image: "./softly-baby.webp",
        rating: 4.5,
        reviews: 45,
        badge: "New",
        isNew: true,
        link: "https://knotsandkrafts.in/shop/66d40068c96124ed7616857e",
      },
      {
        id: 6,
        name: "Papatya Cake Silver",
        price: 299.99,
        originalPrice: null,
        image: "./papatya-cake.jpg",
        rating: 4.7,
        reviews: 23,
        badge: "New",
        isNew: true,
        link: "https://knotsandkrafts.in/shop/66d40068c96124ed76168975",
      },
      {
        id: 7,
        name: "Cicibebe Batik",
        price: 229.99,
        originalPrice: null,
        image: "./cicibebe-batik.jpg",
        rating: 4.8,
        reviews: 67,
        badge: "New",
        isNew: true,
        link: "https://knotsandkrafts.in/shop/66d40068c96124ed7616893f",
      },
      {
        id: 8,
        name: "Krafty Chenille Baby Collection",
        price: 499.99,
        originalPrice: null,
        image: "./chenille.jpg",
        rating: 4.6,
        reviews: 34,
        badge: "New",
        isNew: true,
        link: "https://knotsandkrafts.in/shop/66d40068c96124ed761689ad",
      },
    ],
    [],
  );

  // Memoized categories data
  const categories = useMemo(
    () => [
      {
        title: "Cakes",
        imageSrc: "./cake.jpg",
        link: "/shop?categories=eecaa18f-4a16-4bee-9926-d5d6f8e570ab",
        span: "",
      },
      {
        title: "Accessories",
        imageSrc: "./needles.webp",
        link: "/shop?categories=24728c3b-7f0d-4b26-a7bb-4f52d0194d94",
        span: "md:col-span-2",
      },
      {
        title: "Cotton",
        imageSrc: "./cotton2.jpg",
        link: "/shop?categories=3c9e0495-3504-40db-9162-53c357b172ca&categories=ef86a718-1330-453e-aaf2-d5212d0d7993",
        span: "",
      },
      {
        title: "Wool",
        imageSrc: "./wool.jpg",
        link: "/shop?categories=bc8fbc6a-144e-4b18-99a5-5bc2db9b6119",
        span: "md:col-span-2",
      },
    ],
    [],
  );

  // Memoized company logos data
  const companies = useMemo(
    () => [
      { name: "Krafty", logo: "/companies/krafty.jpg" },
      { name: "Papatya", logo: "/companies/papatya.jpg" },
      { name: "StyleKraft", logo: "/companies/stylekraft.jpg" },
      { name: "Pony", logo: "/companies/pony.jpg" },
      { name: "Ice", logo: "/companies/ice.jpg" },
      { name: "Cicibebe", logo: "/companies/cicibebe.jpg" },
    ],
    [],
  );

  // Memoized accessories data
  const accessoriesData = useMemo(
    () => ({
      topSelling: [
        {
          id: 1,
          name: "Crochet Hooks Set",
          image: "/needles.webp",
          price: 299,
          originalPrice: 399,
          rating: 4.8,
          reviews: 156,
          badge: "Best Seller",
          link: "#",
        },
        {
          id: 2,
          name: "Knitting Needles",
          image: "/needles_with_cloth.webp",
          price: 199,
          rating: 4.6,
          reviews: 89,
          badge: "Popular",
          link: "#",
        },
        {
          id: 3,
          name: "Stitch Markers",
          image: "/cotton.webp",
          price: 149,
          rating: 4.7,
          reviews: 234,
          badge: "New",
          link: "#",
        },
        {
          id: 4,
          name: "Measuring Tape",
          image: "/woolen.webp",
          price: 99,
          rating: 4.5,
          reviews: 67,
          link: "#",
        },
      ],
      newProducts: [
        {
          id: 5,
          name: "Cable Needles",
          image: "/cotton2.jpg",
          price: 179,
          rating: 4.9,
          reviews: 45,
          badge: "New",
          link: "#",
        },
        {
          id: 6,
          name: "Row Counter",
          image: "/wool.jpg",
          price: 129,
          rating: 4.4,
          reviews: 78,
          badge: "New",
          link: "#",
        },
        {
          id: 7,
          name: "Scissors Set",
          image: "/chenille.jpg",
          price: 249,
          originalPrice: 299,
          rating: 4.8,
          reviews: 123,
          badge: "Sale",
          link: "#",
        },
        {
          id: 8,
          name: "Pattern Holder",
          image: "/memories.jpg",
          price: 189,
          rating: 4.6,
          reviews: 56,
          badge: "New",
          link: "#",
        },
      ],
    }),
    [],
  );

  // GSAP animation for categories - optimized with useCallback
  const animateCategories = useCallback(() => {
    // Clear previous refs to prevent duplication
    categoriesRef.current = categoriesRef.current.slice(0, categories.length);

    // Only animate if elements exist and page is ready
    if (categoriesRef.current.length > 0 && pageReady) {
      gsap.fromTo(
        categoriesRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1.5,
          ease: "power4.out",
          stagger: 0.3,
        },
      );
    }
  }, [categories.length, pageReady]);

  useEffect(() => {
    if (pageReady) {
      animateCategories();
    }
  }, [animateCategories, pageReady]);

  // Show loader while page is loading
  if (pageLoading) {
    return <PageLoader message="Loading your crafting paradise..." />;
  }

  return (
    <div className="bg-white">
      {/* Modern Hero Slider Section */}
      <section className="relative mb-16">
        <Suspense fallback={<SkeletonHero />}>
          {heroLoading ? (
            <div className="relative h-96 md:h-[500px] lg:h-[600px] bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="h-8 bg-gray-300 rounded-lg w-64 mb-4 animate-pulse"></div>
                  <div className="h-6 bg-gray-300 rounded-lg w-96 mb-6 animate-pulse"></div>
                  <div className="h-12 bg-gray-300 rounded-lg w-48 animate-pulse"></div>
                </div>
              </div>
            </div>
          ) : (
            <HeroSlider />
          )}
        </Suspense>
      </section>

      {/* Yarns Section - Horizontal Scroll */}
      <Suspense fallback={<SkeletonHorizontalScroll items={4} />}>
        {productsLoading ? (
          <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8">
                <div className="h-8 bg-gray-300 rounded-lg w-48 mx-auto mb-2 animate-pulse"></div>
                <div className="h-6 bg-gray-300 rounded-lg w-64 mx-auto animate-pulse"></div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse"
                  >
                    <div className="h-48 bg-gray-300 rounded-lg mb-4"></div>
                    <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-300 rounded w-1/2 mb-2"></div>
                    <div className="h-6 bg-gray-300 rounded w-1/3"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : (
          <HorizontalProductScroll
            title="Yarns"
            subtitle=""
            tagline=""
            description="Discover our curated collection of the finest yarns, carefully selected for quality, comfort, and creativity."
            topSellingProducts={topSellingYarns}
            newProducts={newYarns}
            viewAllLink="/shop/yarns"
            viewAllText="View All Yarns"
          />
        )}
      </Suspense>

      {/* Accessories Section - Horizontal Scroll */}
      <Suspense fallback={<SkeletonHorizontalScroll items={4} />}>
        {productsLoading ? (
          <section className="py-12 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8">
                <div className="h-8 bg-gray-300 rounded-lg w-48 mx-auto mb-2 animate-pulse"></div>
                <div className="h-6 bg-gray-300 rounded-lg w-64 mx-auto animate-pulse"></div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse"
                  >
                    <div className="h-48 bg-gray-300 rounded-lg mb-4"></div>
                    <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-300 rounded w-1/2 mb-2"></div>
                    <div className="h-6 bg-gray-300 rounded w-1/3"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : (
          <HorizontalProductScroll
            title="Accessories"
            subtitle=""
            tagline=""
            description="Complete your crafting journey with our selection of high-quality accessories and tools."
            topSellingProducts={mergedTopSellingAccessoryIds}
            newProducts={mergedNewAccessoryIds}
            viewAllLink="/shop/accessories"
            viewAllText="View All Accessories"
            tab1Label="Top Selling"
            tab2Label="New Accessories"
          />
        )}
      </Suspense>

      {/* Company Logos - Modern Enhanced Design */}
      <section className="relative bg-gradient-to-br from-gray-50 via-white to-gray-50 py-8 md:py-16 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-100/30 to-accent-100/30 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-accent-100/30 to-primary-100/30 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile: Single Column, Desktop: Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Column - Image Only */}
            <div className="relative order-2 lg:order-1">
              <img
                src="/memories.jpg"
                alt="Knots & Krafts Premium Partners"
                className="w-full h-64 md:h-80 lg:h-96 object-cover"
                loading="lazy"
              />
            </div>

            {/* Right Column - All Content */}
            <div className="relative order-1 lg:order-2">
              {/* Enhanced Header */}
              <div className="text-center lg:text-left mb-6 lg:mb-8">
                <div className="inline-flex items-center px-3 md:px-4 py-1.5 md:py-2 bg-gradient-to-r from-primary-50 to-accent-50 border border-primary-100 mb-3 md:mb-4">
                  <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-primary-600 mr-1.5 md:mr-2" />
                  <span className="text-xs md:text-sm font-medium text-primary-700">
                    Premium Partners
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-secondary-900 mb-3 md:mb-4">
                  Trusted by{" "}
                  <span className="text-primary-600">Leading Brands</span>
                </h2>
                <p className="text-sm md:text-base lg:text-lg text-secondary-600 leading-relaxed">
                  We collaborate with the most prestigious brands in the
                  crafting industry to bring you the finest quality materials.
                </p>
              </div>

              {/* Brands Slider */}
              <div className="relative">
                <div className="overflow-hidden">
                  <div className="flex animate-scroll-smooth">
                    {[...companies, ...companies].map((company, index) => (
                      <div
                        key={`${company.name}-${index}`}
                        className="group flex-shrink-0 relative"
                      >
                        {/* Simplified design - no box, no gradient, just the image */}
                        <div className="relative p-3 md:p-4 lg:p-6 transition-all duration-500">
                          {/* Content */}
                          <div className="relative z-10">
                            {/* Brand Logo - responsive sizing */}
                            <div className="w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 mx-auto flex items-center justify-center">
                              <img
                                src={company.logo}
                                alt={company.name}
                                className="h-full w-full object-contain transition-all duration-300"
                                loading="lazy"
                              />
                            </div>

                            {/* Trust indicator - kept but simplified */}
                            <div className="flex items-center justify-center mt-2 md:mt-3 lg:mt-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                              <div className="flex items-center space-x-1">
                                <div className="w-1 h-1 md:w-1.5 md:h-1.5 bg-green-500 rounded-full animate-pulse-glow-green"></div>
                                <span className="text-xs text-green-600 font-medium">
                                  Verified Partner
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories - Professional Design */}
      <section className="py-8 bg-white">
        <div className="mx-auto max-w-7xl py-8 sm:px-4 px-2 lg:px-8">
          {/* Section Title */}
          <h2 className="text-2xl md:text-4xl font-bold text-center font-poppins mb-8 md:mb-12 text-gray-800">
            Categories
          </h2>

          {/* Categories Grid */}
          {categoriesLoading ? (
            <div className="max-w-full mx-auto grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="relative h-40 xs:h-48 sm:h-56 md:h-64 lg:h-72 bg-gray-300 rounded-lg animate-pulse"
                >
                  <div className="absolute bottom-4 left-4">
                    <div className="h-6 bg-white rounded w-24 mb-2"></div>
                    <div className="h-4 bg-white rounded w-16"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="max-w-full mx-auto grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
              {categories.map((category) => (
                <a
                  key={category.title}
                  href={category.link}
                  className={`relative overflow-hidden group shadow-lg ${category.span}`}
                  ref={(el) => {
                    if (el) {
                      categoriesRef.current[
                        categories.findIndex(
                          (cat) => cat.title === category.title,
                        )
                      ] = el;
                    }
                  }}
                >
                  {/* Category Image */}
                  <img
                    src={category.imageSrc}
                    alt={category.title}
                    className="w-full h-40 xs:h-48 sm:h-56 md:h-64 lg:h-72 object-cover transform transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                    onLoad={(e) => {
                      e.target.style.opacity = "1";
                    }}
                    style={{
                      opacity: 0,
                      transition: "opacity 0.3s ease-in-out",
                    }}
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-60 transition-opacity duration-500 flex items-center justify-center">
                    <div className="text-center px-2 md:px-4">
                      <button className="btn relative uppercase font-bold bg-transparent text-white border-2 border-white py-2 px-4 md:py-3 md:px-8 text-base md:text-lg overflow-hidden transition-all duration-300 ease-in-out hover:bg-primary-600 hover:text-black hover:shadow-lg">
                        {category.title}
                      </button>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            <div className="bg-white border border-gray-200 p-8">
              <h2 className="text-2xl font-semibold mb-4 text-secondary-900">
                Get in Touch
              </h2>
              <p className="text-sm text-secondary-600 mb-6">
                Have questions about our products or need crafting advice? We're
                here to help!
              </p>

              <div className="space-y-4">
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-secondary-600 mr-3" />
                  <div>
                    <h3 className="font-medium text-base text-secondary-900">
                      Visit Our Store
                    </h3>
                    <p className="text-sm text-secondary-600">
                      Knots & Krafts
                      <br />
                      302, JSJ Opal Apartments, BHEL Officers Layout,
                      <br />
                      Jayanagar, Bengaluru, Karnataka 560041, India
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Phone className="h-5 w-5 text-secondary-600 mr-3" />
                  <div>
                    <h3 className="font-medium text-base text-secondary-900">
                      Call Us
                    </h3>
                    <p className="text-sm text-secondary-600">
                      +91 94804 24751
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Mail className="h-5 w-5 text-secondary-600 mr-3" />
                  <div>
                    <h3 className="font-medium text-base text-secondary-900">
                      Email Us
                    </h3>
                    <p className="text-sm text-secondary-600">
                      <a
                        href="mailto:knots.krafts@gmail.com"
                        className="hover:underline hover:text-primary-600 transition-colors"
                      >
                        knots.krafts@gmail.com
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-secondary-600 mr-3" />
                  <div>
                    <h3 className="font-medium text-base text-secondary-900">
                      Business Hours
                    </h3>
                    <p className="text-sm text-secondary-600">
                      Mon-Sat: 9:00 AM - 6:00 PM
                      <br />
                      Sunday: 9:00 AM - 12:00 PM
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-8">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Send us a Message
              </h3>
              <form
                onSubmit={handleContactSubmit}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 bg-white border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-secondary-900 transition-all duration-200"
                    placeholder="Your name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 bg-white border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-secondary-900 transition-all duration-200"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={contactForm.subject}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        subject: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 bg-white border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-secondary-900 transition-all duration-200"
                    placeholder="What's this about?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Message
                  </label>
                  <textarea
                    rows="3"
                    name="message"
                    value={contactForm.message}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        message: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 bg-white border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-secondary-900 transition-all duration-200"
                    placeholder="Tell us how we can help..."
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSending}
                  className="w-full bg-secondary-900 text-white hover:bg-black py-2 px-4 font-medium transition-colors duration-200 border-2 border-secondary-900 hover:border-black disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSending ? (
                    <>
                      <LightLoader size="sm" className="mr-2" />
                      Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
});

HomePage.displayName = "HomePage";

export default HomePage;
